import _ from "lodash";
import FieldNames from "../defaults/FieldNames.json";
import FeatureService from "../services/FeatureService";
import {
  calculateFiscalYearQuarter, formatDateForDisplay,
  getSaveableDate,
  sortFeatureAttributesByDate
} from "./DateHelper";
import { queryAllSummaryService } from "./SummaryHelper";
import MeasurementService from "../services/MeasurementService";
import { getByLevel } from "./CategoryAssociationHelper";
import FormGroupings from "../components/details/FormGroupings.json";
import esriRequest from '@arcgis/core/request';

//Return departure index label based on pixel value
export function getDepartureIndexValueForLabel(value) {
  switch (value) {
      case "I.A":
        return 9;
      case "I.B":
        return 25;
      case "II.A":
        return 42;
      case "II.B":
        return 58;
      case "III.A":
        return 75;
      case "III.B":
        return 91;
      case "Water":
        return 111;
      case "Snow/Ice":
        return 112;
      case "Developed":
        return 120;
      case "Barren or Sparse":
        return 132;
      case "Agriculture":
        return 180;
    default:
      return null;
  }
}

export function getDepartureOptions(){
  return ["I.A", "I.B", "II.A", "II.B", "III.A", "III.B"];
}

export function getConditionClassBasedOnDepartureIndex(value){
  switch (true){
    case (value === null):
      return null;
    case (value < 17):
      return "I.A";
    case (value < 34):
      return "I.B";
    case (value < 50):
      return "II.A";
    case (value < 67):
      return "II.B";
    case (value < 83):
      return "III.A";
    case (value <= 100):
      return "III.B";
    default:
      return null;
  }

}

//Return departure index label based on pixel value
export function getDepartureIndexForValue(value) {

  if (value === null || value === undefined) {
    return "";
  }


  switch (true)
  {
    case (value < 17):
      return "I.A";
    case (value < 34):
      return "I.B";
    case (value < 50):
      return "II.A";
    case (value < 67):
      return "II.B";
    case (value < 83):
      return "III.A";
    default:
      return "III.B";
  }
}

export function isFeatureTreatment(isDraggable, featureAttr, featureFieldsValues) {
  let isFeatureTreatment = true;
  if(featureAttr){
    if(isDraggable){
      isFeatureTreatment = featureAttr.IsTreatment;
    } else {
      isFeatureTreatment = featureAttr[featureFieldsValues.class] === FieldNames.classTypes.treatment || featureAttr[featureFieldsValues.class] === FieldNames.classTypes.actualTreatment;
    }
  }
  return isFeatureTreatment;
}

export function addMeasurementEntry(feature, joinFeature, joinID, isNew, userInfo){
  return new Promise(function(resolve) {
    if(isNew){
      MeasurementService.addMeasurementEntry(
        feature, joinID, joinFeature, userInfo[FieldNames.userFields.nifcUsername]).then(response => {
          resolve(response);
      });
    } else {
      MeasurementService.updateMeasurementEntry(
        feature, joinID, joinFeature, userInfo[FieldNames.userFields.nifcUsername]).then(response => {
          resolve(response);
      });
    }

  });
}

/**
 * Maps the attributes of an Estimated Feature to its Actual counterpart, adjusting and clearing fields as needed
 * @param tempFeatureAttr - Object with additional parameters to pull from
 * @param plannedFeatureAttr - Estimated Object to pull most properties from
 * @param isCurrentTreatment - Whether the new Actual is a treatment or not
 * @param userInfo - User info passed through for setting modifiedby field
 * @returns Object that represents the Actual feature.
 */
export function createFutureActualFeature(tempFeatureAttr, plannedFeatureAttr, isCurrentTreatment, userInfo, isFromSave = false) {
  if(plannedFeatureAttr){
    //Need to pull in the newly derived values from the actual
    let futureActualFeature = tempFeatureAttr ? Object.assign({}, plannedFeatureAttr, tempFeatureAttr) : Object.assign({}, plannedFeatureAttr);
    let fields;
    let classType;
    if(isCurrentTreatment){
      fields = FieldNames.featureFields;
      classType = FieldNames.classTypes.actualTreatment;
    } else {
      fields = FieldNames.featureFields;
      classType = FieldNames.classTypes.actualActivity;
    }
    futureActualFeature[fields.objectID] = null;
    futureActualFeature[fields.id] = null;
    futureActualFeature[FieldNames.EntityID] = null;
    futureActualFeature[FieldNames.featureFields.statusReason] = null;
    futureActualFeature[FieldNames.featureFields.class] = classType;
    futureActualFeature[FieldNames.featureFields.estimatedPersonnelCost] = null;
    futureActualFeature[FieldNames.featureFields.estimatedAssetCost] = null;
    futureActualFeature[FieldNames.featureFields.estimatedGrantsFixedCost] = null;
    futureActualFeature[FieldNames.featureFields.estimatedContractualCost] = null;
    futureActualFeature[FieldNames.featureFields.estimatedOtherCost] = null;
    futureActualFeature[FieldNames.featureFields.contributedPersonnelCost] = null;
    futureActualFeature[FieldNames.featureFields.contributedAssetCost] = null;
    futureActualFeature[FieldNames.featureFields.contributedGrantsFixedCost] = null;
    futureActualFeature[FieldNames.featureFields.contributedContractualCost] = null;
    futureActualFeature[FieldNames.featureFields.contributedOtherCost] = null;
    futureActualFeature[FieldNames.featureFields.localApprovalDate] = null;
    futureActualFeature[FieldNames.featureFields.regionalApprovalDate] = null;
    futureActualFeature[FieldNames.featureFields.agencyApprovalDate] = null;
    futureActualFeature[FieldNames.featureFields.departmentApprovalDate] = null;
    futureActualFeature[FieldNames.featureFields.unApprovalDate] = null;
    futureActualFeature[FieldNames.featureFields.unApprovalReason] = null;
    futureActualFeature[FieldNames.featureFields.fundedDate] = null;
    futureActualFeature[FieldNames.featureFields.isApproved] = null;
    futureActualFeature[FieldNames.featureFields.isFunded] = null;
    if(classType === FieldNames.classTypes.actualActivity){
      futureActualFeature[FieldNames.featureFields.vegDeparturePercentageDerived] = null;
      futureActualFeature[FieldNames.featureFields.vegDeparturePercentageManual] = null;
      futureActualFeature[FieldNames.featureFields.isVegetationManual] = null;
    }
    if(userInfo){
      futureActualFeature[FieldNames.featureFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
      futureActualFeature[FieldNames.featureFields.lastModifiedDate] = getSaveableDate(new Date());
    }

    // TODO: Remove isFromSave with FUELS-1386
    if(!isFromSave && (!tempFeatureAttr || !tempFeatureAttr.WFFTSCopyNewActual)){
      futureActualFeature[fields.completionDate] = null;
      futureActualFeature[fields.completionFiscalQuarter] = "";
      futureActualFeature[fields.completionFiscalYear] = null;
    } else if(tempFeatureAttr && tempFeatureAttr.WFFTSCopyNewActual) {
      if( tempFeatureAttr && tempFeatureAttr[fields.category]){
        futureActualFeature[fields.category] = tempFeatureAttr[fields.category];
      }
      if(tempFeatureAttr && tempFeatureAttr[fields.type]){
        futureActualFeature[fields.type] = tempFeatureAttr[fields.type];
      }
    }

    if(futureActualFeature[FieldNames.featureFields.initiationDate])
    {
      let featureFiscalYear = calculateFiscalYearQuarter(futureActualFeature[FieldNames.featureFields.initiationDate], true);
      let currentFiscalYear = calculateFiscalYearQuarter(new Date(), true);
      if(featureFiscalYear < (currentFiscalYear - 1))
      {
        futureActualFeature[fields.initiationDate] = null;
        futureActualFeature[fields.initiationFiscalQuarter] = "";
        futureActualFeature[fields.initiationFiscalYear] = null;
      }
    }

    // Calculate Status
    if(futureActualFeature[fields.initiationDate] && !futureActualFeature[fields.completionDate]){
      futureActualFeature[fields.status] = FieldNames.statuses.started
    } else if(futureActualFeature[fields.initiationDate] && futureActualFeature[fields.completionDate]) {
      futureActualFeature[fields.status] = FieldNames.statuses.completed
    } else {
      futureActualFeature[fields.status] = FieldNames.statuses.notStarted
    }
    return futureActualFeature;
  } else {
    return {}
  }
}

export function getWFFTSModifiedAttributes(featureAttr, isTreatment, isActual, wfftsAttributes) {
  if(wfftsAttributes) {
    if(wfftsAttributes[FieldNames.wfftsFields.vegDepartureIndex] && isTreatment){
      featureAttr[FieldNames.featureFields.vegDeparturePercentageDerived] = getDepartureIndexValueForLabel(wfftsAttributes[FieldNames.wfftsFields.vegDepartureIndex]);
      featureAttr[FieldNames.featureFields.vegDeparturePercentageManual] = getDepartureIndexValueForLabel(wfftsAttributes[FieldNames.wfftsFields.vegDepartureIndex]);
      featureAttr[FieldNames.featureFields.isVegetationManual] = FieldNames.yesNo.no;
    }
    if(wfftsAttributes[FieldNames.wfftsFields.gisAcres]){
      featureAttr[FieldNames.featureFields.calculatedArea] = wfftsAttributes[FieldNames.wfftsFields.gisAcres];
    }
    let initDate;
    if(wfftsAttributes[FieldNames.wfftsFields.initiationDate]){
      initDate = wfftsAttributes[FieldNames.wfftsFields.initiationDate];
    }

    if(initDate){
      featureAttr[FieldNames.featureFields.initiationDate] = getSaveableDate(initDate);
      featureAttr[FieldNames.featureFields.initiationFiscalYear] = calculateFiscalYearQuarter(initDate, true);
      featureAttr[FieldNames.featureFields.initiationFiscalQuarter] = calculateFiscalYearQuarter(initDate, false);
    }

    if(!isActual){
      featureAttr[FieldNames.featureFields.name] = wfftsAttributes[FieldNames.wfftsFields.treatmentName] ?? wfftsAttributes[FieldNames.wfftsFields.treatmentName];

      let compDate;
      if(wfftsAttributes[FieldNames.wfftsFields.completionDate]){
        compDate = wfftsAttributes[FieldNames.wfftsFields.completionDate];
      }
      if(compDate){
        featureAttr[FieldNames.featureFields.completionDate] = getSaveableDate(compDate);
        featureAttr[FieldNames.featureFields.completionFiscalYear] = calculateFiscalYearQuarter(compDate, true);
        featureAttr[FieldNames.featureFields.completionFiscalQuarter] = calculateFiscalYearQuarter(compDate, false);
      }

    }
  }
  return featureAttr;
}

export function getLegacyModifiedAttributes(featureAttr, legacyAttributes, fields) {
  if(legacyAttributes){
    if(legacyAttributes.GISAcres) featureAttr[fields.calculatedArea] = legacyAttributes.GISAcres;
    if(legacyAttributes.VegDepartureIndex) featureAttr[fields.vegDeparturePercentageManual] = getDepartureIndexValueForLabel(legacyAttributes.VegDepartureIndex);
  }
  return featureAttr;
}

export function isFeaturePlannedOrActual(isDraggable, feature, featureFieldsValues) {
  let isFeatureActuals = false;
  if(feature){
    if(isDraggable){
      isFeatureActuals = feature.IsActuals;
    } else {
      isFeatureActuals = feature[featureFieldsValues.class] === FieldNames.classTypes.actualTreatment || feature[featureFieldsValues.class] === FieldNames.classTypes.actualActivity;
    }
  }
  return isFeatureActuals;
}

export function createFeatureObject(featureAttr, sequenceNumber) {
  let obj = {};
  obj[FieldNames.featureFields.id] = featureAttr[FieldNames.featureFields.id] ? featureAttr[FieldNames.featureFields.id] : "--";
  obj[FieldNames.featureFields.joinID] = featureAttr[FieldNames.featureFields.joinID] ?? "--";
  obj[FieldNames.featureFields.joinFeature] = featureAttr[FieldNames.featureFields.joinFeature] ?? "--";
  obj[FieldNames.featureFields.objectID] = featureAttr[FieldNames.featureFields.objectID];
  obj[FieldNames.featureFields.name] = featureAttr[FieldNames.featureFields.name] ? featureAttr[FieldNames.featureFields.name] : "--";
  obj[FieldNames.featureFields.category] = featureAttr[FieldNames.featureFields.category] ? featureAttr[FieldNames.featureFields.category] : "--";
  obj[FieldNames.featureFields.class] = featureAttr[FieldNames.featureFields.class] ? featureAttr[FieldNames.featureFields.class] : "--";
  obj[FieldNames.featureFields.type] = featureAttr[FieldNames.featureFields.type] ? featureAttr[FieldNames.featureFields.type] : "--";
  obj[FieldNames.featureFields.calculatedArea] = featureAttr[FieldNames.featureFields.calculatedArea] ? featureAttr[FieldNames.featureFields.calculatedArea] : "--";
  obj[FieldNames.featureFields.estimatedTotalCost] = featureAttr[FieldNames.featureFields.estimatedTotalCost] ? featureAttr[FieldNames.featureFields.estimatedTotalCost] : "--";
  obj[FieldNames.featureFields.initiationFiscalYear] = featureAttr[FieldNames.featureFields.initiationFiscalYear] ? featureAttr[FieldNames.featureFields.initiationFiscalYear] : "--";
  obj[FieldNames.featureFields.completionFiscalYear] = featureAttr[FieldNames.featureFields.completionFiscalYear] ? featureAttr[FieldNames.featureFields.completionFiscalYear] : "--";
  obj[FieldNames.featureFields.localApprovalDate] = featureAttr[FieldNames.featureFields.localApprovalDate] ? featureAttr[FieldNames.featureFields.localApprovalDate] : null;
  obj[FieldNames.featureFields.priority] = featureAttr[FieldNames.featureFields.priority] ? featureAttr[FieldNames.featureFields.priority] : "--";
  obj[FieldNames.featureFields.feasibility] = featureAttr[FieldNames.featureFields.feasibility] ? featureAttr[FieldNames.featureFields.feasibility] : "--";
  obj[FieldNames.featureFields.estimatedSuccessProbability] = featureAttr[FieldNames.featureFields.estimatedSuccessProbability] ? featureAttr[FieldNames.featureFields.estimatedSuccessProbability] : "--";
  obj[FieldNames.featureFields.status] = featureAttr[FieldNames.featureFields.isBIL] === FieldNames.yesNo.yes ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
  obj[FieldNames.featureFields.state] = featureAttr[FieldNames.featureFields.state] ? featureAttr[FieldNames.featureFields.state] : "--";
  obj[FieldNames.featureFields.fundingUnit] = featureAttr[FieldNames.featureFields.fundingUnit] ? featureAttr[FieldNames.featureFields.fundingUnit] : "--";
  obj[FieldNames.featureFields.initiationDate] = featureAttr[FieldNames.featureFields.initiationDate];
  obj[FieldNames.featureFields.completionDate] = featureAttr[FieldNames.featureFields.completionDate];
  obj[FieldNames.featureFields.initiationFiscalYear] = featureAttr[FieldNames.featureFields.initiationFiscalYear] ? featureAttr[FieldNames.featureFields.initiationFiscalYear] : "--";
  obj[FieldNames.featureFields.completionFiscalYear] = featureAttr[FieldNames.featureFields.completionFiscalYear] ? featureAttr[FieldNames.featureFields.completionFiscalYear] : "--";
  obj[FieldNames.featureFields.status] = featureAttr[FieldNames.featureFields.status] ? featureAttr[FieldNames.featureFields.status] : "--";
  obj[FieldNames.featureFields.groupCount] = featureAttr[FieldNames.featureFields.groupCount] ? featureAttr[FieldNames.featureFields.groupCount] : 0;
  obj[FieldNames.featureFields.hasActual] = featureAttr[FieldNames.featureFields.hasActual] ? featureAttr[FieldNames.featureFields.hasActual] : FieldNames.yesNo.no;
  obj[FieldNames.featureFields.hasGroup] = featureAttr[FieldNames.featureFields.hasGroup] ? featureAttr[FieldNames.featureFields.hasGroup] : FieldNames.yesNo.no;
  obj[FieldNames.featureFields.isBIL] = featureAttr[FieldNames.featureFields.isBIL] ? featureAttr[FieldNames.featureFields.isBIL] : FieldNames.yesNo.no;
  obj["ActivityID"] = featureAttr[FieldNames.featureFields.class] === FieldNames.classTypes.activity ? featureAttr[FieldNames.featureFields.id] : null;
  obj["TreatmentID"] = featureAttr[FieldNames.featureFields.class] === FieldNames.classTypes.treatment ? featureAttr[FieldNames.featureFields.id] : null;
  obj["ActualActivityID"] = featureAttr[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity ? featureAttr[FieldNames.featureFields.id] : null;
  obj["ActualTreatmentID"] = featureAttr[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment ? featureAttr[FieldNames.featureFields.id] : null;
  obj["EntityID"] = featureAttr[FieldNames.featureFields.id];
  obj[FieldNames.featureFields.entityType] = featureAttr[FieldNames.featureFields.entityType];
  if(sequenceNumber !== null){
    obj["SequenceNumber"] = sequenceNumber
  }

  obj.classID = `${featureAttr[FieldNames.featureFields.class]}-${featureAttr[FieldNames.featureFields.objectID]}`;

  return obj
}

export function isUpdateFullySuccessful(response) {
  let isFullySuccessful = false;
  if(response && response.failures.length === 0){
    isFullySuccessful = true;
  } else {
    isFullySuccessful = false;
  }
  return isFullySuccessful;
}

export function isUpdatePartiallySuccessful(response) {
  let isAtLeastPartiallySuccessful = false;
  if(response && response.failures.length === 0){
    isAtLeastPartiallySuccessful = true;
  } else {
    if(response && response.successful.length > 0){
      isAtLeastPartiallySuccessful = true;
    } else {
      isAtLeastPartiallySuccessful = false;
    }
  }
  return isAtLeastPartiallySuccessful;
}

export function getRequiredFields (item, isTreatment, agencyValue, userInfo, isActuals, isProject=false) {
  let requiredFields = [];
  if(item?.attributes){
    let itemAttr = item.attributes;
    requiredFields = getRequiredFieldsDirect(itemAttr, isTreatment, agencyValue, userInfo, isActuals, isProject)
  }
  else if(item)
  {
    requiredFields = getRequiredFieldsDirect(item, isTreatment, agencyValue, userInfo, isActuals, isProject)
  }
  return requiredFields;
}

// values are in item instead of item.attributes
function getRequiredFieldsDirect (item, isTreatment, agencyValue, userInfo, isActuals, isProject=false) {
  let requiredFields = [];
  if(item){
    const actualReqFields = (isTreatment ? FieldNames.actualTreatmentRequiredFields : FieldNames.actualActivityRequiredFields);
    const estimatedReqFields = (isTreatment ? FieldNames.requiredFields : FieldNames.activityRequiredFields);
    const projectReqFields = FieldNames.projectRequiredFields;
    if(isProject){
      requiredFields = _.cloneDeep(projectReqFields)
    } else {
      requiredFields = _.cloneDeep(isActuals ? actualReqFields : estimatedReqFields);
      if(requiredFields?.length){
        requiredFields = adjustDynamicRequiredFields(requiredFields, isTreatment, isActuals, userInfo, item, isProject);
      }
    }
  }
  return requiredFields;
}

// The design intent for this method is that the requiredFields from FieldNames should be fields that are always required,
// and this method conditionally adds any fields that are not always required, as opposed to filtering any out.
function adjustDynamicRequiredFields(requiredFields, isTreatment, isActuals, userInfo, feature, isProject=false){
  const fields = isProject ? FieldNames.projectFields : FieldNames.featureFields;
  const isPostFire = isFeaturePostFire(feature, isProject);
  // Cancellation Logic
  if(!isProject){
    if(feature[fields.status] === FieldNames.statuses.cancelled && !requiredFields.includes(fields.statusReason)){
      requiredFields.push(`${fields.statusReason}`);
    }

    // Category Affected Logic
    if(!feature[fields.category] || feature[fields.category] !== FieldNames.categoriesForChecking.programManagement){
      if(isTreatment && !requiredFields.includes(fields.estimatedSuccessProbability))
      {
        requiredFields.push(`${fields.estimatedSuccessProbability}`);
      }
      if(isTreatment && !requiredFields.includes(fields.durability))
      {
        requiredFields.push(`${fields.durability}`);
      }
      if(!requiredFields.includes(fields.feasibility))
      {
        requiredFields.push(`${fields.feasibility}`);
      }
      if(!requiredFields.includes(fields.priority))
      {
        requiredFields.push(`${fields.priority}`);
      }
    }

    const subTypePossibleValues = getByLevel(feature, FieldNames.categoryAssociationFields.subTypes)

    if(subTypePossibleValues?.length > 0 && isPostFire){
      requiredFields.push(`${fields.subType}`)
    }

    if(isPostFire && !isActuals){
      requiredFields.push(`${fields.program}`)
    }

    //WBSID logic for actuals - Required for all Actuals and only required on estimated
    //if BLM (code is in Funding Agency Dependent Logic)
    if(!requiredFields.includes(fields.wbsID) && isActuals)
    {
      requiredFields.push(`${fields.wbsID}`);
    }

    // Funding Agency Dependent Logic
    let agencyValue = feature[FieldNames.fundingFields.fundingAgency];
    if(agencyValue){
      // BLM Logic
      if(agencyValue.includes(FieldNames.agencies.blm)){
        if(isTreatment && !requiredFields.includes(fields.treatmentDriver))
        {
          requiredFields.push(`${fields.treatmentDriver}`);
        }
        if(!requiredFields.includes(fields.wbsID) && !isActuals)
        {
          requiredFields.push(`${fields.wbsID}`);
        }
        if(!requiredFields.includes(fields.isSagebrush))
        {
          requiredFields.push(`${fields.isSagebrush}`);
        }
      }

      // BIA Logic
      if(agencyValue.includes(FieldNames.agencies.bia)){
        if(!requiredFields.includes(fields.isRTRL))
        {
          requiredFields.push(`${fields.isRTRL}`);
        }
        if(!requiredFields.includes(fields.fundingTribe) &&
            userInfo && userInfo[FieldNames.userFields.isTribalUser] === FieldNames.yesNo.yes)
        {
          requiredFields.push(`${fields.fundingTribe}`);
        }
      }
    }

    // Funding Category Affected logic
    if(feature[FieldNames.fundingFields.fundingSourceCategory])
    {
      // Only mark FundingSourceSubCategory if the FundingSourceCategory has subcategories
      if(feature[FieldNames.fundingFields.fundingSourceCategory] === FieldNames.budgetBuckets.bil &&
        !requiredFields.includes(fields.fundingSourceSubCategory))
      {
        requiredFields.push(`${fields.fundingSourceSubCategory}`);
      }
    }

    //Measurements
    if(isPostFire && isTreatment){
      if(!requiredFields.includes(fields.measureAmount)){
        requiredFields.push(`${fields.measureAmount}`)
      }

      if(!requiredFields.includes(fields.unitOfMeasure)){
        requiredFields.push(`${fields.unitOfMeasure}`)
      }
    }

    if(isPostFire){
      requiredFields = requiredFields.filter(field => {
        return field !== FieldNames.featureFields.isWUI && field !== FieldNames.featureFields.isVegetationManual && field !== FieldNames.featureFields.vegDeparturePercentageDerived && field !== FieldNames.featureFields.vegDeparturePercentageManual && field !== FieldNames.featureFields.durability && field !== FieldNames.featureFields.isRTRL;
      });
    }

    requiredFields = requiredFields.filter(field => {
      return (!feature[field] || !(
        (field === fields.department && feature[field] === 'N/A' && feature[FieldNames.featureFields.locationDepartmentNaValid] === FieldNames.yesNo.yes)
        || (field === fields.agency && feature[field] === 'N/A' && feature[FieldNames.featureFields.locationAgencyNaValid] === FieldNames.yesNo.yes)
        || (field === fields.region && feature[field] === 'N/A' && feature[FieldNames.featureFields.locationRegionNaValid] === FieldNames.yesNo.yes)
        || (field === fields.unit && feature[field] === 'N/A' && feature[FieldNames.featureFields.locationUnitNaValid] === FieldNames.yesNo.yes)
      ))
    });
  }
  return requiredFields;
}

/**
 * Returns object that may have had it's fields array altered through another method based on conditional criteria.
 * Expectation is that isTreatment and feature is never null.
 * @param isTreatment - Whether we are passing in a Treatment or Activity
 * @param feature - feature of item
 * @param isFromActual - Whether this is for an actual or estimated
 * @returns - An object for either the Treatment Form Groupings or Activity Form Grouping with conditional fields calculated
 */
export function getFormGroupings (isTreatment, feature, isFromActual) {
  const isPostFire = isFeaturePostFire(feature?.attributes);
  let formDetails;
  if(isFromActual){
    formDetails = isTreatment ? FormGroupings?.actualTreatmentDetails : FormGroupings?.actualActivityDetails;
  } else {
    formDetails = isTreatment ? FormGroupings?.treatmentDetails : FormGroupings?.activityDetails
  }
  if(isPostFire){
    formDetails = adjustDynamicFormGroupings(formDetails)
  }
  return formDetails;
}

/**
 * Returns object that may have had it's fields array altered based on conditional criteria.
 * Expectation is that formDetails will be returned only with field changes, if the criteria is met.
 * @param formDetails - Original version of formDetails from FormGroupings. Either treatmentDetails or activityDetails
 * @returns - An object either in the original form or with fields modified
 */
function adjustDynamicFormGroupings (formDetails) {
  //This is only called in cases that are post fire
  if(formDetails){
    let tempGroupDetails = _.cloneDeep(formDetails);

    let contextPanelFields = tempGroupDetails?.filter(x => x.identifier === FieldNames.panels.context)?.map(x => x.fields)?.[0]
    let proposalPanelFields = tempGroupDetails?.filter(x => x.identifier === FieldNames.panels.proposal)?.map(x => x.fields)?.[0]

    if(!contextPanelFields?.includes(FieldNames.featureFields.program) || (proposalPanelFields?.includes(FieldNames.featureFields.isInteragency) || proposalPanelFields?.includes(FieldNames.featureFields.interagencySupport))){
      tempGroupDetails?.forEach((eaPanel) => {
        if(eaPanel.identifier === FieldNames.panels.context){
          eaPanel.fields.unshift(FieldNames.featureFields.program)
        }
        if(eaPanel.identifier === FieldNames.panels.proposal){
          const limitedFields = eaPanel.fields.filter(x => x !== FieldNames.featureFields.isInteragency && x !== FieldNames.featureFields.interagencySupport);
          eaPanel.fields = limitedFields;
        }
      })
      formDetails = tempGroupDetails
    }
  }
  return formDetails
}

//Being used by Details Container and Approval Data Grid
export function getAllMissingFields (feature, isTreatment, userInfo, isActuals, isProject=false)
{
  let missingFields;
  let featureAttr = feature?.attributes ?? feature;
  missingFields = getAllMissingFieldsDirect(featureAttr, isTreatment, userInfo, isActuals, isProject);
  return missingFields;
}

function getAllMissingFieldsDirect (feature, isTreatment, userInfo, isActuals, isProject=false) {
  let missingFields;
  let costFields = FieldNames?.costFields;
  const fields = isProject ? FieldNames?.projectFields : FieldNames?.featureFields;

  if(feature){
    const requiredFields = getRequiredFieldsDirect(feature, isTreatment, feature?.[FieldNames.featureFields.fundingAgency], userInfo, isActuals, isProject);
    missingFields = requiredFields;
    if(missingFields?.length){
      if(!isProject){
        if(feature?.[FieldNames.featureFields.fundingDepartment]){
          missingFields = missingFields.filter(field => field !== fields.fundingDepartment);
        }

        if(feature?.[FieldNames.featureFields.fundingAgency]){
          missingFields = missingFields.filter(field => field !== FieldNames.featureFields.fundingAgency);
        }

        if(feature?.[FieldNames.featureFields.fundingRegion]){
          missingFields = missingFields.filter(field => field !== FieldNames.featureFields.fundingRegion);
        }

        if(feature?.[FieldNames.featureFields.fundingUnit]){
          missingFields = missingFields.filter(field => field !== FieldNames.featureFields.fundingUnit);
        }

        if(feature?.[FieldNames.featureFields.fundingTribe]){
          missingFields = missingFields.filter(field => field !== FieldNames.featureFields.fundingTribe);
        }
      }

      missingFields = missingFields.filter(field => {
        if(costFields?.includes(field) && !isProject){
          return(feature[field] === null || feature[field] === undefined ||
            feature[field] === "" ||
            (isTreatment && field === fields.vegDeparturePercentageManual && (feature[field] > 100 || feature[field] < 0)) ||
            (field === fields.estimatedTotalCost && feature[field] <= 0));
        } else {
          return (feature[field] === null
            || feature[field] === undefined
            || feature[field] === ""
            || (field === fields.wbsID && ((
              feature[FieldNames?.featureFields?.fundingAgency] &&
              feature[FieldNames?.featureFields?.fundingAgency]?.includes(FieldNames?.agencies?.blm) &&
              (!feature[field] || (feature[field].length !== 4 && (feature[field].length !== 14 || feature[FieldNames?.featureFields?.wbsID].substring(0, 6) !== FieldNames?.wbsPrefixes?.blm))))
              || feature[field] === "XXXXXXXXXXXX"))
            || ((field === fields.department ||
              field === fields.agency ||
              field === fields.region ||
              field === fields.unit) && (feature && feature[field] && feature[field].toLowerCase() === "n/a"))
            || (isTreatment && field === fields.vegDeparturePercentageManual && (feature[field] > 100 ||
              feature[field] < 0)))
        }
      });
    }
  }
  return missingFields;
}

//Using for List Card
//Currently only using this for the card colors in the list and only for Estimated 8-2-23
export function getAllMissingFieldsCount (feature, isTreatment, userInfo, isActuals, filterFields = null) {
  let count = 0;
  if(feature){
    let missingFields = getAllMissingFields(feature, isTreatment, userInfo, isActuals);

    if(filterFields && filterFields.length > 0)
    {
      filterFields.forEach(fieldToFilter => {
        missingFields = missingFields.filter(field => field !== fieldToFilter);
      });
    }

    if(missingFields){
      count =  missingFields.length;
    }
  }
  return (count <= 0) ? 0 : count;
}

export function getStatus ( editableFeature, hasActual, isActual, missingFieldsCount) {
  let currentStatus = "";
  let editableFeatureAttr = editableFeature?.attributes ?? editableFeature;
  currentStatus = getStatusDirect(editableFeatureAttr, hasActual, isActual, missingFieldsCount);
  return currentStatus;
}

export function getStatusDirect (editableFeature, hasActual, isActual, missingFieldsCount, attributeName = null, attributeValue = null) {
  let currentStatus = "";
  let tempFeature = editableFeature;
  if(editableFeature){
    if(attributeName){
      tempFeature[attributeName] = attributeValue;
    }
    if(!isActual && (!hasActual || hasActual === FieldNames.yesNo.no)){
      //Approved (Local), Approved (Regional), Approved (Agency), Approved (Department), Ready for Approval, Draft
      if(tempFeature[FieldNames.featureFields.cancelledDate])
      {
        currentStatus = FieldNames.statuses.cancelled;
      } else if(missingFieldsCount > 0){
        currentStatus = FieldNames.statuses.draft;
      } else {
        if(tempFeature[FieldNames.sharedFeatureTableFields.unapprovalDate]){
          currentStatus = FieldNames.statuses.unApprovalRequested;
        }
        else if(!tempFeature[FieldNames.featureFields.localApprovalDate] && !tempFeature[FieldNames.featureFields.regionalApprovalDate] &&
          !tempFeature[FieldNames.featureFields.agencyApprovalDate] && !tempFeature[FieldNames.featureFields.departmentApprovalDate]){
          currentStatus = FieldNames.statuses.readyForApproval;
        } else if(tempFeature[FieldNames.featureFields.localApprovalDate] && !tempFeature[FieldNames.featureFields.regionalApprovalDate] && !tempFeature[FieldNames.featureFields.agencyApprovalDate] && !tempFeature[FieldNames.featureFields.departmentApprovalDate]){
          currentStatus = FieldNames.statuses.approvedLocal;
        } else if(tempFeature[FieldNames.featureFields.regionalApprovalDate] && !tempFeature[FieldNames.featureFields.agencyApprovalDate] && !tempFeature[FieldNames.featureFields.departmentApprovalDate]){
          currentStatus = FieldNames.statuses.approvedRegional;
        } else if(tempFeature[FieldNames.featureFields.agencyApprovalDate] && !tempFeature[FieldNames.featureFields.departmentApprovalDate]){
          currentStatus = FieldNames.statuses.approvedAgency;
        } else if(tempFeature[FieldNames.featureFields.departmentApprovalDate]){
          currentStatus = FieldNames.statuses.approvedDepartment;
        }
      }
    } else {
      if(tempFeature[FieldNames.featureFields.cancelledDate])
      {
        currentStatus = FieldNames.statuses.cancelled;
      } else if(!tempFeature[FieldNames.featureFields.initiationDate] && !tempFeature[FieldNames.featureFields.completionDate]){
        currentStatus = FieldNames.statuses.notStarted;
      } else if (tempFeature[FieldNames.featureFields.initiationDate] && !tempFeature[FieldNames.featureFields.completionDate]){
        currentStatus = FieldNames.statuses.started;
      } else if(tempFeature[FieldNames.featureFields.completionDate]){
        currentStatus = FieldNames.statuses.completed;
      }
    }
  }
  return currentStatus;
}

export function getFieldDisplayValue (fieldValue, name, allFields) {
  let tempVal = fieldValue;
  if(tempVal && tempVal.includes("|")){
    if(name === allFields.fundingDepartment){
      tempVal = "Multiple Funding Departments";
    } else if(name === allFields.fundingAgency){
      tempVal = "Multiple Funding Agencies";
    } else if(name === allFields.fundingRegion){
      tempVal = "Multiple Funding Regions";
    } else if(name === allFields.fundingUnit){
      tempVal = "Multiple Funding Units";
    } else if(name === allFields.fundingSubUnit){
      tempVal = "Multiple Funding SubUnits";
    } else if(name === allFields.fundingTribe){
      tempVal = "Multiple Funding Tribes";
    } else if(name === allFields.fundingSource){
      tempVal = "Multiple Funding Sources";
    } else if(name === allFields.fundingSourceProgram) {
      tempVal = "Multiple Funding Source Programs";
    } else if(name === allFields.fundingSourceCategory) {
      tempVal = "Multiple Funding Source Categories";
    } else if(name === allFields.fundingSourceSubCategory) {
      tempVal = "Multiple Funding Source SubCategories";
    } else if(name === allFields.fundingUnitType){
      tempVal = "Multiple Funding Unit Types";
    }
  }
  return tempVal;
}

export const getSummaryFundingRowsByActionObjectIds = (allItemIds) => {
  if(allItemIds?.length > 0)
  {
    const itemJoinStr = allItemIds.join(",");
    const defExpression = `OBJECTID IN (${itemJoinStr})`;
    return queryAllSummaryService(defExpression, "*");
  }
  else
  {
    return new Promise((resolve, reject) => {
      resolve([]);
    });
  }
}

export const getSummaryFundingRowsForProgramOfWork = (fiscalYear) => {
  if(fiscalYear)
  {
    let baseWhereClause = `((EntityType = '${FieldNames.internalEntityTypes.fuels}') AND ((Class='${FieldNames.classTypes.treatment}' AND DepartmentApprovalDate IS NOT NULL) OR (Class='${FieldNames.classTypes.actualTreatment}' AND CompletionDate IS NOT NULL)) AND (CancelledDate IS NULL AND Status <> '${FieldNames.statuses.cancelled}'))`;
    let year = Number(fiscalYear?.FiscalYear ?? fiscalYear);
    baseWhereClause = baseWhereClause + ` AND ((CompletionFiscalYear IS NULL AND InitiationFiscalYear = ${year}) OR (CompletionFiscalYear IS NOT NULL AND CompletionFiscalYear = ${year}))`;
    baseWhereClause = baseWhereClause + `AND ((FundingDepartment IS NOT NULL) AND (FundingAgency IS NOT NULL) AND (FundingRegion IS NOT NULL) AND (FundingUnit IS NOT NULL))`;
    return queryAllSummaryService(baseWhereClause, "*");
  }
  else
  {
    return new Promise((resolve, reject) => {
      resolve([]);
    });
  }
}

export const getEntityTypeByClass = (classVal) =>
{
  const entityType = (classVal === FieldNames.classTypes.treatment ? FieldNames.entityTypes.treatment :
                        (classVal === FieldNames.classTypes.activity ? FieldNames.entityTypes.activity :
                          (classVal === FieldNames.classTypes.actualTreatment ? FieldNames.entityTypes.actualTreatment :
                            (classVal === FieldNames.classTypes.actualActivity ? FieldNames.entityTypes.actualActivity : "ERROR")
                          )
                        )
                      );
  return entityType;
}

export const getWhereClauseForFeaturesByFundingFields = (department, agency, region, unit, subunit, tribeFullName,
  fiscalYear, entityType, additionalWhereClause, useConcatFields = false) => {
  let baseWhereClause = "";
  let departmentExists = department && department[FieldNames.budgetFields.department];
  let agencyExists = agency && agency[FieldNames.budgetFields.agency];
  let regionExists = region && region[FieldNames.budgetFields.region];
  let unitExists = unit && unit[FieldNames.budgetFields.unit];
  let subUnitExists = subunit && subunit[FieldNames.budgetFields.subUnit];
  let tribeExists = tribeFullName && tribeFullName[FieldNames.budgetFields.constructedTribeFullName];
  let year = fiscalYear && fiscalYear.FiscalYear && Number(fiscalYear.FiscalYear);
  let concatAdditionString = useConcatFields ? 'Concat' : '';

  baseWhereClause =
    baseWhereClause +
    (departmentExists
    ? ` AND (${concatAdditionString}FundingDepartment LIKE '%${
    department[FieldNames.budgetFields.department]
    }%')`
    : ``);
  baseWhereClause =
    baseWhereClause +
    (agencyExists
    ? ` AND (${concatAdditionString}FundingAgency LIKE '%${agency[FieldNames.budgetFields.agency]}%')`
    : ``);
  baseWhereClause =
    baseWhereClause +
    (regionExists
    ? ` AND (${concatAdditionString}FundingRegion LIKE '%${region[FieldNames.budgetFields.region]}%')`
    : ``);
  baseWhereClause =
    baseWhereClause +
    (unitExists
    ? ` AND (${concatAdditionString}FundingUnit LIKE '%${unit[FieldNames.budgetFields.unit]}%')`
    : ``);
  baseWhereClause =
    baseWhereClause +
    (subUnitExists
    ? ` AND (${concatAdditionString}FundingSubUnit LIKE '%${subunit[FieldNames.budgetFields.subUnit]}%')`
    : ``);
  baseWhereClause =
    baseWhereClause +
    (tribeExists
    ? ` AND (${concatAdditionString}FundingTribe LIKE '%${
    tribeFullName[FieldNames.budgetFields.constructedTribeFullName]
    }%')`
    : ``);

  // Use the Completion -> Initiation FY setup for now
  baseWhereClause = baseWhereClause + (year ?
  ` AND ((CompletionFiscalYear IS NULL AND InitiationFiscalYear = ${year}) OR (CompletionFiscalYear IS NOT NULL AND CompletionFiscalYear = ${year}))`
  : ``);

  baseWhereClause = baseWhereClause + (entityType ?
    ` AND (EntityType = '${entityType}')`
    : ``);

  if(!baseWhereClause){
    baseWhereClause = "(OBJECTID IS NOT NULL)";
  }

  // additionalWhereClause is expected to come with the ` AND ` prefix
  let combinedWhereClause = baseWhereClause + (additionalWhereClause ? additionalWhereClause : ``);
  let andCheck = combinedWhereClause.substring(0,5) === " AND ";
  let trimmedWhereClause = andCheck ? combinedWhereClause.substring(5) : combinedWhereClause;
  let finalClause = "(" + trimmedWhereClause + ")";
  return finalClause;
}

export const isFeaturePostFire = (featureAttr, isProject=false) => {
  if(!featureAttr) return false;
  let feature = featureAttr.attributes ?? featureAttr;
  if(isProject){
    // TODO: When we have FUELS projects, check entityType but default to postfire/true
    return true;
  } else {
    const isPostFire = (feature[FieldNames.featureFields.entityType] === FieldNames.internalEntityTypes.postFire ||
      feature[FieldNames.featureFields.entityType] === FieldNames.internalEntityTypes.actualPostFireActivity ||
      feature[FieldNames.featureFields.entityType] === FieldNames.internalEntityTypes.actualPostFireTreatment ||
      feature[FieldNames.featureFields.entityType] === FieldNames.internalEntityTypes.estimatedPostFireActivity ||
      feature[FieldNames.featureFields.entityType] === FieldNames.internalEntityTypes.estimatedPostFireTreatment) ?
      true : false;

    return isPostFire
  }
}

export const getFeatureListCardTitle = (feature, userInfo) => {
  let featureName = `${FieldNames.classTypes.treatment}`;
  if(userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes && userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes){
    if(feature[FieldNames.featureFields.class]){
      if(feature[FieldNames.featureFields.entityType] === FieldNames.internalEntityTypes.postFire){
        featureName = `${feature[FieldNames.featureFields.class]} - ${FieldNames.internalEntityTypes.postFire}`;
      } else {
        featureName = `${feature[FieldNames.featureFields.class]} - ${FieldNames.internalEntityTypes.fuels}`;
      }
    }
  } else {
    if(feature[FieldNames.featureFields.class]){
      featureName = `${feature[FieldNames.featureFields.class]}`;
    }
  }

  return featureName;
}

export const getProjectListCardTitle = (feature) => {
  let featureName = `${FieldNames.classTypes.treatment}`;
  if(feature[FieldNames.featureFields.entityType] === FieldNames.internalEntityTypes.fuels){
    featureName = `Fuels Project`;
  } else {
    featureName = `Post Fire Project`;
  }

  return featureName;
}


